<script setup lang="ts">
import VButton from "@verbleif/lib/src/components/Button/VButton.vue";
import DialogTitle from "@verbleif/lib/src/components/Dialog/DialogTitle.vue";
import VIcon from "@verbleif/lib/src/components/Icon/VIcon.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { COUNTDOWN_DURATION, UpdateReason, useAppUpdate } from "../../composables/AppUpdate/useAppUpdate";

const {
  updateReason,
  canDismiss,
  isApplying,
  remainingSeconds,
  applyUpdate,
  dismissUpdate,
} = useAppUpdate();
const { t } = useI18n();

const isChunkError = computed(() => updateReason.value === UpdateReason.CHUNK_LOAD_ERROR);
const progressPercent = computed(() => (remainingSeconds.value / COUNTDOWN_DURATION) * 100);
</script>

<template>
  <div class="flex flex-col w-full">
    <DialogTitle>{{ t("notifications.type.app_update") }}</DialogTitle>
    <div class="px-5">
      <div class="flex flex-col items-center justify-center text-center">
        <VIcon
          v-if="isChunkError"
          name="triangle-alert"
          class="text-(--warning)"
          size="2em"
        />
        <VIcon
          v-else
          name="cloud-download"
          class="text-(--primary)"
          size="2em"
        />
        <div class="mt-4 text-center w-full">
          {{ isChunkError ? t("notifications.local_update") : t("notifications.app_update") }}
        </div>
      </div>
      <div class="flex gap-1.5 justify-between py-5">
        <VButton
          v-if="canDismiss"
          variant="gray"
          :disabled="isChunkError"
          rounded
          @click="dismissUpdate"
        >
          {{ t("notifications.setup_enable_cancel") }}
        </VButton>
        <VButton
          variant="primary"
          rounded
          class="ml-auto"
          icon-left="refresh-cw"
          :loading="isApplying"
          @click="applyUpdate"
        >
          {{ t("notifications.install_update") }}
        </VButton>
      </div>
    </div>
    <div>
      <div class="flex items-center justify-between px-5 pb-2">
        <span class="text-xs text-(--text-2)">
          {{ t("notifications.auto_update_countdown", { seconds: remainingSeconds }) }}
        </span>
      </div>
      <div
        class="h-1 w-full bg-(--background-dark)"
        role="progressbar"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-valuenow="Math.round(progressPercent)"
        :aria-label="t('notifications.auto_update_countdown', { seconds: remainingSeconds })"
      >
        <div
          class="h-full bg-(--primary) transition-[width] duration-1000 ease-linear"
          :style="{ width: `${progressPercent}%` }"
        />
      </div>
    </div>
  </div>
</template>
